import { ButtonHTMLAttributes } from 'react';
import { TDS } from '../../constants/tds';

type Variant = 'primary' | 'secondary' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
}

const variantStyles: Record<Variant, { bg: string; color: string; border: string }> = {
  primary: {
    bg: TDS.color.blue,
    color: TDS.color.textWhite,
    border: 'none',
  },
  secondary: {
    bg: TDS.color.bgTertiary,
    color: TDS.color.textPrimary,
    border: 'none',
  },
  ghost: {
    bg: 'transparent',
    color: TDS.color.textSecondary,
    border: `1.5px solid ${TDS.color.gray200}`,
  },
};

// 디자인 기준 (2026-04-19): lg는 17px/700/height 56px/padding 2px 28px.
type SizeSpec = {
  padding: string;
  fontSize: string;
  fontWeight: number;
  borderRadius: string;
  height?: number;
};

const sizeStyles: Record<Size, SizeSpec> = {
  sm: { padding: '8px 14px', fontSize: '13px', fontWeight: 600, borderRadius: TDS.radius.sm },
  md: { padding: '12px 20px', fontSize: '15px', fontWeight: 600, borderRadius: TDS.radius.md },
  lg: { padding: '2px 28px', fontSize: '17px', fontWeight: 700, borderRadius: TDS.radius.lg, height: 56 },
};

export function TdsButton({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  disabled,
  children,
  style,
  ...rest
}: Props) {
  const v = variantStyles[variant];
  const s = sizeStyles[size];
  const isDisabled = disabled || loading;

  // 디자인 기준: disabled는 배경색 교체가 아닌 opacity 0.4로 "흐린 파란 버튼"을 유지.
  return (
    <button
      disabled={isDisabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        width: fullWidth ? '100%' : undefined,
        height: s.height,
        padding: s.padding,
        backgroundColor: v.bg,
        color: v.color,
        border: v.border,
        borderRadius: s.borderRadius,
        fontSize: s.fontSize,
        fontWeight: s.fontWeight,
        opacity: isDisabled ? 0.4 : 1,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit',
        transition: 'opacity 0.15s',
        ...style,
      }}
      {...rest}
    >
      {loading ? <LoadingDots color={v.color} /> : children}
      <style>{`
        @keyframes tds-dot-pulse {
          0%, 80%, 100% { opacity: .3; transform: scale(.8); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </button>
  );
}

// 디자인 기준: 스피너 원 대신 staggered pulse 점 3개.
function LoadingDots({ color }: { color: string }) {
  const dot = (delay: string) => (
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: color,
        display: 'inline-block',
        animation: `tds-dot-pulse 1.2s ease-in-out ${delay} infinite both`,
      }}
    />
  );
  return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
      {dot('0s')}
      {dot('0.15s')}
      {dot('0.3s')}
    </span>
  );
}
